import React from 'react';
import { Button, Heading } from '@legalshield/adonis-ux-framework';

import { AllTaskValues, CurrencyAmount, TaskKey } from '../types';

type SummaryRow = { label: string; value: string };

const fmtCurrency = (c: CurrencyAmount | undefined) => (c?.display ? c.display : '—');

const fmtDate = (iso: string | undefined): string => {
  if (!iso) return '';
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return iso;
  const [, yyyy, mm, dd] = match;
  return `${mm}/${dd}/${yyyy}`;
};

const fmtPhone = (raw: string | undefined): string => {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return raw;
};

const buildRows = (key: TaskKey, values: AllTaskValues): { title: string; rows: SummaryRow[] } => {
  switch (key) {
    case 'task1': {
      const v = values.task1;
      return {
        title: 'Name',
        rows: [
          { label: 'First Name', value: v?.firstName ?? '' },
          { label: 'Middle Name', value: v?.middleName ?? '' },
          { label: 'Last Name', value: v?.lastName ?? '' },
        ],
      };
    }
    case 'task2': {
      const v = values.task2;
      return {
        title: 'Contact',
        rows: [
          { label: 'Email', value: v?.email ?? '' },
          { label: 'Phone - Office', value: fmtPhone(v?.phone_OFFICE) },
          { label: 'Phone - Mobile', value: v?.phone_MOBILE ? fmtPhone(v.phone_MOBILE) : '—' },
        ],
      };
    }
    case 'task3': {
      const v = values.task3;
      const addr = v?.addressForm;
      const addrString = addr
        ? [addr.address1, addr.address2, addr.city, addr.state, addr.zipCode].filter(Boolean).join(', ')
        : '';
      return {
        title: 'Firm',
        rows: [
          { label: 'Firm Name', value: v?.firmName ?? '' },
          { label: 'Website', value: v?.websiteUrl ?? '—' },
          { label: 'Address', value: addrString },
        ],
      };
    }
    case 'task4':
      return {
        title: 'Practice Start',
        rows: [{ label: 'Start Date', value: fmtDate(values.task4?.startDate) }],
      };
    case 'task5': {
      const licenses = values.task5?.licenses ?? [];
      return {
        title: 'Licenses',
        rows: licenses.map((l, i) => ({
          label: `License #${i + 1}`,
          value: `${l.countryAndState.country} ${l.countryAndState.states.map((s) => s.value).join(', ')} · Bar ${
            l.barNumber
          } · ${fmtDate(l.admissionDate)}`,
        })),
      };
    }
    case 'task6': {
      const areas = values.task6?.areasOfLaw ?? [];
      return {
        title: 'Areas of Law',
        rows: areas.map((a) => ({
          label: a.category,
          value: a.choices.join(', '),
        })),
      };
    }
    case 'task7': {
      const excl = values.task7?.areaOfLawExclusions ?? [];
      return {
        title: 'Exclusions',
        rows: excl.map((e, i) => ({
          label: `Exclusion #${i + 1}`,
          value: `${e.areaOfLaw} in ${e.stateAndCounty.state} (${e.stateAndCounty.counties
            .map((c) => c.title)
            .join(', ')})`,
        })),
      };
    }
    case 'task8':
      return {
        title: 'Hourly Rate',
        rows: [{ label: 'Amount', value: fmtCurrency(values.task8?.amount) }],
      };
    case 'task9': {
      const ex = values.task9?.hourlyRateExceptions ?? [];
      return {
        title: 'Hourly Exceptions',
        rows: ex.map((e, i) => ({
          label: `#${i + 1} ${e.areaOfLaw}`,
          value: fmtCurrency(e.amount),
        })),
      };
    }
    case 'task10': {
      const ex = values.task10?.fixedRates ?? [];
      return {
        title: 'Fixed Rates',
        rows: ex.map((e, i) => ({
          label: `#${i + 1} ${e.areaOfLaw}`,
          value: fmtCurrency(e.amount),
        })),
      };
    }
    case 'task11': {
      const v = values.task11;
      return {
        title: 'Disciplinary Action',
        rows: [
          { label: 'Answer', value: v?.disciplinaryAction ?? '' },
          ...(v?.details ? [{ label: 'Details', value: v.details }] : []),
        ],
      };
    }
    case 'task12': {
      const langs = values.task12?.languages ?? [];
      return {
        title: 'Languages',
        rows: [{ label: 'Selected', value: langs.map((l) => l.title).join(', ') || '—' }],
      };
    }
    case 'task13':
      return {
        title: 'Discount',
        rows: [{ label: '25% Discount', value: values.task13?.discountAgreementAccepted ?? '' }],
      };
    case 'task14': {
      const v = values.task14;
      return {
        title: 'Insurance',
        rows: [
          { label: 'Uploads', value: (v?.attachments ?? []).map((a) => a.filename).join(', ') },
          { label: 'Policy Number', value: v?.policy_number ?? '' },
          { label: 'Carrier', value: v?.policy_carrier ?? '' },
          { label: 'Amount', value: fmtCurrency(v?.policy_amount) },
          { label: 'Expiration', value: fmtDate(v?.policy_expiration) },
        ],
      };
    }
    case 'task15':
      return { title: 'Tax ID', rows: [{ label: 'Tax ID', value: values.task15?.taxId ?? '' }] };
    case 'task16':
      return {
        title: 'Terms',
        rows: [
          {
            label: 'Agreement',
            value: values.task16?.serviceAgreement ? 'I have read and understood the terms and conditions' : '—',
          },
        ],
      };
    default:
      return { title: key, rows: [] };
  }
};

const TASK_KEYS: TaskKey[] = [
  'task1',
  'task2',
  'task3',
  'task4',
  'task5',
  'task6',
  'task7',
  'task8',
  'task9',
  'task10',
  'task11',
  'task12',
  'task13',
  'task14',
  'task15',
  'task16',
];

type Props = {
  allValues: AllTaskValues;
  onEdit: (taskIndex: number) => void;
};

const ConfirmationSummary = ({ allValues, onEdit }: Props) => (
  <div className="rnq-confirmation">
    {TASK_KEYS.map((key, index) => {
      const { title, rows } = buildRows(key, allValues);
      return (
        <div key={key} className="rnq-confirmation__task">
          <div className="rnq-confirmation__header">
            <Heading as="T16" text={title} />
            <Button label="Edit" variant="link" noPadding onClick={() => onEdit(index)} />
          </div>
          {rows.length === 0 ? (
            <p className="rnq-confirmation__row-value">—</p>
          ) : (
            rows.map((row, i) => (
              <div key={i} className="rnq-confirmation__row">
                <span className="rnq-confirmation__row-label">{row.label}</span>
                <span className="rnq-confirmation__row-value">{row.value || '—'}</span>
              </div>
            ))
          )}
        </div>
      );
    })}
  </div>
);

export default ConfirmationSummary;
